import React, { useMemo, useState, useEffect, useLayoutEffect, useId } from 'react';
import clsx from 'clsx';

import styles from './SearchDropDown.module.scss';
import { BrandAndThemeProvider } from 'components/helpers/ContextProvider/BrandAndThemeContext';
import { Themes } from 'lib/themes';

export interface SearchDropDownBaseProps {
  items: string[];
  query?: string;
  onSelect: (value: string) => void;
  onDismiss?: () => void;
  className?: string;
  ariaLabel?: string;
  /** Stable id for the listbox <ul> — allows the owner input to set aria-controls. */
  listboxId?: string;
  /**
   * Ref to the <ul> element — allows the parent to focus the first option via ArrowDown on the input.
   */
  listRef?: React.RefObject<HTMLUListElement | null>;
  /**
   * Ref to the search input so focus can be returned on Escape or ArrowUp from the first item.
   */
  inputRef?: React.RefObject<HTMLInputElement | null>;
  /**
   * Ref to the clear button (if visible) for Tab from last dropdown item.
   */
  clearButtonRef?: React.RefObject<HTMLButtonElement | null>;
  /**
   * Ref to the next focusable element (e.g., search button) for Tab from last item.
   */
  nextFocusableRef?: React.RefObject<HTMLElement | null>;
  /**
   * Ref to the outer wrapper (e.g. form) — used with nextFocusableRef to compute dropdown width.
   */
  wrapperRef?: React.RefObject<HTMLElement | null>;
}

export interface SearchDropDownProps extends SearchDropDownBaseProps {
  theme?: Themes;
}

const MAX_DROPDOWN_ITEMS = 5;

/**
 * Strip all HTML tags — used for aria-label and plain-text keys.
 */
const stripHtml = (text: string) => text.replace(/<[^>]+>/g, '');

/**
 * API-sourced items already carry <em>…</em> around the typed query.
 * Render the <em> portion at normal weight; everything else bold.
 */
const renderApiHighlighted = (text: string) => {
  const parts = text.split(/(<em>.*?<\/em>)/);
  return (
    <>
      {parts.map((part, i) => {
        const emMatch = part.match(/^<em>(.*?)<\/em>$/);
        if (emMatch) {
          // matched query — normal weight
          return <span key={i}>{emMatch[1]}</span>;
        }
        // suggestion context — bold
        return part ? (
          <strong key={i} className={styles.match}>
            {part}
          </strong>
        ) : null;
      })}
    </>
  );
};

const highlightMatch = (text: string, query?: string) => {
  const trimmedQuery = query?.trim();
  if (!trimmedQuery) return text;

  const lowerText = text.toLowerCase();
  const lowerQuery = trimmedQuery.toLowerCase();
  const matchIndex = lowerText.indexOf(lowerQuery);

  if (matchIndex === -1) return text;

  const start = text.slice(0, matchIndex);
  const match = text.slice(matchIndex, matchIndex + trimmedQuery.length);
  const end = text.slice(matchIndex + trimmedQuery.length);

  return (
    <>
      {start}
      <strong className={styles.match}>{match}</strong>
      {end}
    </>
  );
};

export const SearchDropDownBase = ({
  items,
  query,
  onSelect,
  onDismiss,
  className,
  ariaLabel = 'Suggested searches',
  listRef,
  inputRef,
  listboxId: listboxIdProp,
  clearButtonRef,
  nextFocusableRef,
  wrapperRef,
}: SearchDropDownBaseProps) => {
  const generatedId = useId();
  const listboxId = listboxIdProp ?? generatedId;
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
  const usesPopoverRef = React.useRef(false);

  const filteredItems = useMemo(() => {
    const normalizedQuery = query?.trim().toLowerCase();
    const matched = normalizedQuery
      ? items.filter((item) => item.toLowerCase().includes(normalizedQuery))
      : items;
    return matched.slice(0, MAX_DROPDOWN_ITEMS);
  }, [items, query]);

  useLayoutEffect(() => {
    const el = listRef?.current;
    if (!el || typeof el.showPopover !== 'function') return;

    usesPopoverRef.current = true;
    el.setAttribute('popover', 'manual');
    el.showPopover();

    return () => {
      try {
        el.hidePopover();
      } catch {
        // element may already be removed from the DOM
      }
    };
  }, [listRef]);

  // Keep dropdown size and position in sync with the wrapper
  useLayoutEffect(() => {
    if (!wrapperRef?.current) return;
    const el = listRef?.current;

    const updatePosition = () => {
      if (!usesPopoverRef.current || !el) return;
      const rect = wrapperRef.current!.getBoundingClientRect();
      el.style.top = `${rect.bottom}px`;
      el.style.left = `${rect.left}px`;
    };

    const updateSize = () => {
      const wrapperW = wrapperRef.current?.offsetWidth ?? 0;
      const buttonW = (nextFocusableRef?.current as HTMLElement)?.offsetWidth ?? 0;
      const width = wrapperW > 0 ? wrapperW - buttonW : undefined;

      if (usesPopoverRef.current) {
        const rect = wrapperRef.current!.getBoundingClientRect();
        setDropdownStyle({
          width,
          top: rect.bottom,
          left: rect.left,
        });
      } else {
        setDropdownStyle({ width });
      }
    };

    updateSize();
    window.addEventListener('resize', updateSize);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updateSize);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [wrapperRef, nextFocusableRef, listRef]);

  useEffect(() => {
    const list = listRef?.current;
    if (!list) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      const options = Array.from(list.querySelectorAll<HTMLLIElement>('[role="option"]'));
      const currentIndex = options.indexOf(document.activeElement as HTMLLIElement);

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          options[(currentIndex + 1) % options.length]?.focus();
          break;
        case 'ArrowUp':
          event.preventDefault();
          if (currentIndex <= 0) {
            inputRef?.current?.focus();
          } else {
            options[currentIndex - 1]?.focus();
          }
          break;
        case 'Enter':
          event.preventDefault();
          if (currentIndex >= 0) {
            onSelect(stripHtml(filteredItems[currentIndex]));
            inputRef?.current?.focus();
          }
          break;
        case 'Escape':
          event.preventDefault();
          onDismiss?.();
          inputRef?.current?.focus();
          break;
      }
    };

    list.addEventListener('keydown', handleKeyDown);
    return () => list.removeEventListener('keydown', handleKeyDown);
  }, [listRef, inputRef, onSelect, onDismiss, filteredItems, clearButtonRef, nextFocusableRef]);

  if (!filteredItems.length) return null;

  const listContent = (
    <ul
      ref={listRef}
      id={listboxId}
      className={clsx(styles.dropdownList, className)}
      style={dropdownStyle}
      role="group"
      aria-label={ariaLabel}
    >
      {filteredItems.map((item, index) => {
        const hasApiHighlight = item.includes('<em>');
        const plainText = hasApiHighlight ? stripHtml(item) : item;
        const displayContent = hasApiHighlight
          ? renderApiHighlighted(item)
          : highlightMatch(item, query);
        return (
          <li
            key={`${plainText}-${index}`}
            id={`${listboxId}-${index}`}
            role="option"
            aria-selected={activeIndex === index}
            tabIndex={-1}
            className={clsx(styles.dropdownItem, activeIndex === index && styles.active)}
            aria-label={`Search for ${plainText}`}
            onMouseEnter={() => setActiveIndex(index)}
            onFocus={() => setActiveIndex(index)}
            onMouseDown={(event) => {
              event.preventDefault();
              onSelect(plainText);
            }}
          >
            <span className={styles.dropdownText}>{displayContent}</span>
          </li>
        );
      })}
    </ul>
  );

  return listContent;
};

export const SearchDropDown = ({ theme, ...props }: SearchDropDownProps) => {
  return (
    <BrandAndThemeProvider
      theme={theme}
      data-component="authorable/shared/navigation/searchdropdown/searchdropdown"
    >
      <SearchDropDownBase {...props} />
    </BrandAndThemeProvider>
  );
};
